import { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Stack,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import intl from 'components/i18n/ReactIntlWrapper';
import ResizableDrawer from 'components/Layout/ResizableDrawer';
import RichTextEditor from 'components/common/RichTextEditor';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  item: any;
  seniorities: any[];
  educationLevels: any[];
}

const JobOpeningDrawer: React.FC<Props> = ({ open, onClose, onSave, item, seniorities, educationLevels }) => {
  const [form, setForm] = useState({
    title: '', projectDescription: '', roleDescription: '', remarks: '', requiredExperience: '',
    idSeniority: '' as number | '', idEducation: '' as number | '',
  });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      title: item?.title ?? '',
      projectDescription: item?.projectDescription ?? '',
      roleDescription: item?.roleDescription ?? '',
      remarks: item?.remarks ?? '',
      requiredExperience: item?.requiredExperience?.toString() ?? '',
      idSeniority: item?.idSeniority ?? item?.seniority?.id ?? '',
      idEducation: item?.idEducation ?? item?.educationLevel?.id ?? '',
    });
    setStartDate(item?.startDate ? new Date(item.startDate) : null);
  }, [item, open]);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim() || !form.idSeniority || !startDate) return;
    setSaving(true);
    try {
      await onSave({
        title: form.title.trim(),
        startDate: startDate!.toISOString(),
        projectDescription: (form.projectDescription && form.projectDescription !== '<p></p>') ? form.projectDescription : null,
        roleDescription: (form.roleDescription && form.roleDescription !== '<p></p>') ? form.roleDescription : null,
        remarks: (form.remarks && form.remarks !== '<p></p>') ? form.remarks : null,
        requiredExperience: form.requiredExperience ? parseInt(form.requiredExperience) : null,
        idSeniority: Number(form.idSeniority),
        idEducation: form.idEducation ? Number(form.idEducation) : null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResizableDrawer open={open} onClose={onClose} defaultWidth={480}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {item ? intl.get('hr.jobOpening.edit') : intl.get('hr.jobOpening.add')}
      </Typography>
      <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <TextField label={intl.get('hr.jobOpening.title')} value={form.title} onChange={set('title')} fullWidth required sx={{ mb: 2 }} />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={intl.get('hr.jobOpening.startDate')}
            value={startDate}
            onChange={(d) => setStartDate(d)}
            slotProps={{ textField: { fullWidth: true, required: true, sx: { mb: 2 } } }}
          />
        </LocalizationProvider>
        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel>{intl.get('hr.jobOpening.seniority')}</InputLabel>
          <Select value={form.idSeniority} onChange={(e) => setForm((f) => ({ ...f, idSeniority: e.target.value as number }))} label={intl.get('hr.jobOpening.seniority')}>
            {seniorities.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </Select>
        </FormControl>
        {educationLevels.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{intl.get('hr.jobOpening.education')}</InputLabel>
            <Select value={form.idEducation} onChange={(e) => setForm((f) => ({ ...f, idEducation: e.target.value as number }))} label={intl.get('hr.jobOpening.education')}>
              <MenuItem value=""><em>{intl.get('common.none')}</em></MenuItem>
              {educationLevels.map((e) => <MenuItem key={e.id} value={e.id}>{e.levelName}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <TextField label={intl.get('hr.jobOpening.experience')} value={form.requiredExperience} onChange={set('requiredExperience')} fullWidth type="number" inputProps={{ min: 0 }} sx={{ mb: 2 }} />
        <Box sx={{ mb: 2 }}>
          <RichTextEditor label={intl.get('hr.jobOpening.projectDescription')} value={form.projectDescription} onChange={(html) => setForm((f) => ({ ...f, projectDescription: html }))} minHeight={100} />
        </Box>
        <Box sx={{ mb: 2 }}>
          <RichTextEditor label={intl.get('hr.jobOpening.roleDescription')} value={form.roleDescription} onChange={(html) => setForm((f) => ({ ...f, roleDescription: html }))} minHeight={100} />
        </Box>
        <Box sx={{ mb: 3 }}>
          <RichTextEditor label={intl.get('hr.jobOpening.remarks')} value={form.remarks} onChange={(html) => setForm((f) => ({ ...f, remarks: html }))} minHeight={80} />
        </Box>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onClose}>{intl.get('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={saving}>{intl.get('common.save')}</Button>
        </Stack>
      </Box>
    </ResizableDrawer>
  );
};

export default JobOpeningDrawer;
